import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { ShoppingCart, User, LogOut, LayoutDashboard, Truck, Menu, X } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import { Button } from "./ui/button";

export default function Navbar() {
  const { user, logout } = useAuth();
  const { count } = useCart();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);

  const navLinkClass = ({ isActive }) =>
    `text-sm font-medium transition-colors ${isActive ? "text-[#2F5233]" : "text-[#4A5D5A] hover:text-[#2F5233]"}`;

  const mobileLinkClass = ({ isActive }) =>
    `block py-3 px-4 rounded-lg text-base font-medium transition-colors ${isActive ? "bg-[#2F5233]/10 text-[#2F5233]" : "text-[#1C2826] hover:bg-[#F1F3EE]"}`;

  const closeMenu = () => setOpen(false);

  return (
    <header className="glass-nav sticky top-0 z-50">
      <div className="max-w-7xl mx-auto flex items-center justify-between px-4 sm:px-6 lg:px-8 h-16">
        <Link to="/" className="flex items-center gap-2 min-w-0 flex-shrink" data-testid="nav-logo" onClick={closeMenu}>
          <img src="/assets/arbe-logo.png" alt="Arbe Minimart" className="h-10 w-10 sm:h-12 sm:w-12 lg:h-14 lg:w-14 object-contain flex-shrink-0" />
          <div className="flex flex-col leading-none gap-0.5 sm:gap-1 min-w-0">
            <span className="font-heading font-extrabold text-[13px] sm:text-base lg:text-lg text-[#C62828] tracking-tight whitespace-nowrap">ARBE MINIMART</span>
            <div className="h-px w-full bg-[#C62828]/40"></div>
            <span className="hidden sm:inline text-[9px] lg:text-[10px] font-bold uppercase tracking-[0.2em] text-[#2F5233] mt-0.5 whitespace-nowrap">Fresh · Local · Delivered</span>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-6 xl:gap-8">
          <NavLink to="/" end className={navLinkClass} data-testid="nav-home">Home</NavLink>
          <NavLink to="/shop" className={navLinkClass} data-testid="nav-shop">Shop</NavLink>
          <NavLink to="/shop?category=Groceries" className={navLinkClass} data-testid="nav-groceries">Groceries</NavLink>
          <NavLink to="/shop?category=Meats" className={navLinkClass} data-testid="nav-meats">Meats</NavLink>
          <NavLink to="/shop?category=Condiments" className={navLinkClass} data-testid="nav-condiments">Condiments</NavLink>
          <NavLink to="/shop?category=Frozen%20Products" className={navLinkClass} data-testid="nav-frozen">Frozen</NavLink>
          <NavLink to="/shop?category=Sunjin%20Feeds" className={navLinkClass} data-testid="nav-sunjin">Sunjin Feeds</NavLink>
        </nav>

        <div className="flex items-center gap-1 sm:gap-2">
          <Link to="/cart" className="relative p-2 rounded-full hover:bg-[#F1F3EE] transition-colors" data-testid="nav-cart" onClick={closeMenu}>
            <ShoppingCart className="h-5 w-5 text-[#1C2826]" strokeWidth={1.75} />
            {count > 0 && (
              <span className="absolute -top-0.5 -right-0.5 bg-[#E67E22] text-white rounded-full text-[10px] font-bold h-5 min-w-5 px-1 flex items-center justify-center" data-testid="nav-cart-count">
                {count}
              </span>
            )}
          </Link>

          {user ? (
            <div className="hidden sm:flex items-center gap-1">
              {(user.role === "admin" || user.role === "manager" || user.role === "cashier") && (
                <Button variant="ghost" size="sm" onClick={() => nav("/admin")} data-testid="nav-admin">
                  <LayoutDashboard className="h-4 w-4 sm:mr-1.5" /> <span className="hidden md:inline">Admin</span>
                </Button>
              )}
              {user.role === "driver" && (
                <Button variant="ghost" size="sm" onClick={() => nav("/driver")} data-testid="nav-driver">
                  <Truck className="h-4 w-4 sm:mr-1.5" /> <span className="hidden md:inline">Driver</span>
                </Button>
              )}
              {user.role === "customer" && (
                <Button variant="ghost" size="sm" onClick={() => nav("/my-orders")} data-testid="nav-my-orders">
                  <User className="h-4 w-4 sm:mr-1.5" /> <span className="hidden md:inline">My Orders</span>
                </Button>
              )}
              <Button variant="ghost" size="sm" onClick={() => { logout(); nav("/"); }} data-testid="nav-logout">
                <LogOut className="h-4 w-4" />
              </Button>
            </div>
          ) : (
            <div className="hidden sm:flex items-center gap-1">
              <Button variant="ghost" size="sm" onClick={() => nav("/login")} data-testid="nav-login">Login</Button>
              <Button size="sm" className="bg-[#2F5233] hover:bg-[#1F3D24] text-white rounded-full px-4" onClick={() => nav("/register")} data-testid="nav-register">
                Sign up
              </Button>
            </div>
          )}

          {/* Mobile hamburger */}
          <button
            className="lg:hidden p-2 rounded-full hover:bg-[#F1F3EE] transition-colors"
            onClick={() => setOpen((v) => !v)}
            aria-label="Toggle menu"
            data-testid="nav-mobile-toggle"
          >
            {open ? <X className="h-5 w-5 text-[#1C2826]" /> : <Menu className="h-5 w-5 text-[#1C2826]" />}
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      {open && (
        <div className="lg:hidden border-t border-[#E1E5DF] bg-white shadow-lg" data-testid="nav-mobile-drawer">
          <div className="max-w-7xl mx-auto px-4 py-3 space-y-1">
            <NavLink to="/" end className={mobileLinkClass} onClick={closeMenu} data-testid="nav-mobile-home">Home</NavLink>
            <NavLink to="/shop" className={mobileLinkClass} onClick={closeMenu} data-testid="nav-mobile-shop">Shop</NavLink>
            <NavLink to="/shop?category=Groceries" className={mobileLinkClass} onClick={closeMenu} data-testid="nav-mobile-groceries">Groceries</NavLink>
            <NavLink to="/shop?category=Meats" className={mobileLinkClass} onClick={closeMenu} data-testid="nav-mobile-meats">Meats</NavLink>
            <NavLink to="/shop?category=Condiments" className={mobileLinkClass} onClick={closeMenu} data-testid="nav-mobile-condiments">Condiments</NavLink>
            <NavLink to="/shop?category=Frozen%20Products" className={mobileLinkClass} onClick={closeMenu} data-testid="nav-mobile-frozen">Frozen</NavLink>
            <NavLink to="/shop?category=Sunjin%20Feeds" className={mobileLinkClass} onClick={closeMenu} data-testid="nav-mobile-sunjin">Sunjin Feeds</NavLink>

            <div className="pt-3 mt-3 border-t border-[#E1E5DF] space-y-1">
              {user ? (
                <>
                  {(user.role === "admin" || user.role === "manager" || user.role === "cashier") && (
                    <button className={mobileLinkClass({ isActive: false }) + " w-full text-left flex items-center gap-2"} onClick={() => { nav("/admin"); closeMenu(); }} data-testid="nav-mobile-admin">
                      <LayoutDashboard className="h-4 w-4" /> Admin Console
                    </button>
                  )}
                  {user.role === "driver" && (
                    <button className={mobileLinkClass({ isActive: false }) + " w-full text-left flex items-center gap-2"} onClick={() => { nav("/driver"); closeMenu(); }} data-testid="nav-mobile-driver">
                      <Truck className="h-4 w-4" /> Driver Portal
                    </button>
                  )}
                  {user.role === "customer" && (
                    <button className={mobileLinkClass({ isActive: false }) + " w-full text-left flex items-center gap-2"} onClick={() => { nav("/my-orders"); closeMenu(); }} data-testid="nav-mobile-my-orders">
                      <User className="h-4 w-4" /> My Orders
                    </button>
                  )}
                  <button className={mobileLinkClass({ isActive: false }) + " w-full text-left flex items-center gap-2 text-[#C62828]"} onClick={() => { logout(); nav("/"); closeMenu(); }} data-testid="nav-mobile-logout">
                    <LogOut className="h-4 w-4" /> Log out
                  </button>
                </>
              ) : (
                <div className="flex gap-2 px-1">
                  <Button variant="outline" className="flex-1 h-11" onClick={() => { nav("/login"); closeMenu(); }} data-testid="nav-mobile-login">Login</Button>
                  <Button className="flex-1 h-11 bg-[#2F5233] hover:bg-[#1F3D24] text-white" onClick={() => { nav("/register"); closeMenu(); }} data-testid="nav-mobile-register">Sign up</Button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
